import { Injectable } from '@angular/core';
import { Schedule, ScheduleComments } from '../../models/schedule.class';
import { SchedulingRecordsService } from './schedule-records.service';
import {
  finalize,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { AnnouncementRecService } from './announcements-records.service';
import { ValidationService } from '../../../../../../services/form-submittion-services/validation.service';
import { DialogBoxService } from '../../../../../../services/components-reusable-services/dialog-box.service';
import { ProdRecService } from '../../../../../../services/form-submittion-services/prod-record-form.service';
import { CalcService } from '../../../../../../services/display-services/calc.service';
import { PDFService } from '../../../../../../services/communication-services/pdf.service';
import { UserRoleService } from '../../../../../../services/user-services/user-roles.service';
import { TableColumn } from '../../../../../../models/table-model.class';
import { ShapesOrder } from '../../../special-shapes/models/shapes.class';
import { CellService } from '../../../../../../services/widget-services/cell.service';
import { FieldEditorService } from '../../../../../../services/widget-services/field-editor.service';
import { PkgGroup } from '../../../package-reports/models/pack-report.class';
import {
  WorkflowRunnerService,
  WorkflowStep,
} from '../../../../../../services/form-submittion-services/work-flow.service';
import { OptimizationService } from '../../../../../../services/display-services/optimization.service';
import {
  Departments,
  entities,
} from '../../../../../../../assets/data/daily-reports.data';
import { CompareRecordsService } from '../../../../../../services/form-submittion-services/compare-records.service';
@Injectable({
  providedIn: 'root',
})
export class SchedulingService {
  emailLoading: boolean = false;
  printLoading: boolean = false;
  constructor(
    private validationService: ValidationService,
    private scheduleRecords: SchedulingRecordsService,
    private announcementRecs: AnnouncementRecService,
    private dialogService: DialogBoxService,
    private prodRecordForm: ProdRecService,
    private calc: CalcService,
    private pdf: PDFService,
    private userRoleService: UserRoleService,
    private cellService: CellService,
    private fieldService: FieldEditorService,
    private workflowRunner: WorkflowRunnerService,
    private optimization: OptimizationService,
    private compare: CompareRecordsService,
  ) {}
  async getCompletedSchedulesByMill(
    mill: number,
    notes: boolean,
    year?: number | string,
  ) {
    const yearVar = year ?? new Date().getFullYear() % 100;

    const recs = await firstValueFrom(
      this.scheduleRecords.fetchCompletedSchedulesByMill(mill, yearVar, notes),
    );

    return this.organizeByRun(recs);
  }
  async getIncompletedSchedulesByMill(mill: number) {
    const recs = await firstValueFrom(
      this.scheduleRecords.fetchIncompleteSchedules(mill, false, true),
    );
    return recs;
  }
  fetchRunNumbers(days?: number): Observable<Schedule[]> {
    const dayAmount = days || 14;
    return this.scheduleRecords.fetchSchedulesByDays(dayAmount);
  }
  async fetchRunNumbersByYear(year: string) {
    return await firstValueFrom(
      this.scheduleRecords.fetchCompletedSchedules(year),
    );
  }
  getNotesByScheduleId(scheduleId: number) {
    return this.scheduleRecords.getNotesByScheduleId(scheduleId);
  }
  fetchMillData(mills: number[]) {
    return this.scheduleRecords.fetchMillData(mills);
  }
  organizeByRun(records: any[]) {
    let recs = records.filter((rec) => rec.run !== '');
    return recs.sort((a, b) => {
      const [firstA, middleA] = a.run.split('-').map(Number);
      const [firstB, middleB] = b.run.split('-').map(Number);
      if (firstA !== firstB) {
        return firstA - firstB; // sort by first two digits
      }
      return middleA - middleB; // then sort by middle three digits
    });
  }
  organizeByIndex(records: any) {
    // sorts by index
    return records.sort((a: any, b: any) => {
      return (a.index || 0) - (b.index || 0);
    });
  }
  async grabSchedulesByMill(
    mill: number,
    applyStatus: boolean,
    applyNotes: boolean,
  ) {
    const recs = await firstValueFrom(
      this.scheduleRecords.fetchIncompleteSchedules(
        mill,
        applyNotes,
        applyStatus,
      ),
    );
    return this.organizeByIndex(recs);
  }

  async grabAnnouncementByMill(mill: number) {
    if (mill === 0) return;
    return await firstValueFrom(
      this.announcementRecs.fetchAnnouncementByMill(mill),
    );
  }
  async createScheduleAndAnnouncement(mill: number, completed?: boolean) {
    const mills = entities
      .filter((e) => e.dept === Departments.Moulding)
      .flatMap((e) => {
        return {
          id: e.values[0].entityId,
          machineNo: e.values.flatMap((v) => v.entityId),
          mill: e.mills[0],
          plantName: e.plantName,
          location: e.location,
          identifier: e.values[0].entityId,
          assets: e.values,
        };
      })
      .filter((m) => m.id !== 0);
    // for each mill it grabs the announcement and makes a new grouped library
    const announcement = await this.grabAnnouncementByMill(Number(mill));
    const completedSchedules = completed || false;
    let schedules = completedSchedules
      ? await this.getCompletedSchedulesByMill(mill, false)
      : await this.grabSchedulesByMill(mill, false, false);
    const millGroup = mills.find((rec) => rec.mill === mill);
    return {
      mill: mill,
      plantName: millGroup ? millGroup.plantName : 'N/a',
      nextAvailable: announcement?.nextAvailable || 'N/A',
      location: millGroup ? millGroup.location : 0,
      runNote: announcement?.runNote || 'N/A',
      date: announcement?.date || 'N/A',
      schedules: schedules,
    };
  }
  loadingFetched: boolean = false;

  fetchAllByRun(runNumber: string) {
    this.loadingFetched = true;

    // First grab all schedules with run number
    return this.scheduleRecords.fetchScheduleByRun(runNumber).pipe(
      switchMap((schedules: Schedule[]) => {
        if (!schedules || schedules.length === 0) {
          return of([]);
        }

        const reportObservables = schedules.map((schedule) =>
          this.fetchAllByScheduleId(schedule),
        );

        return forkJoin(reportObservables).pipe(
          map((nestedArrays) => nestedArrays.flat()),
        );
      }),
      finalize(() => {
        this.loadingFetched = false;
      }),
    );
  }
  fetchScheduleByRun(run: string) {
    return this.scheduleRecords
      .fetchScheduleByRun(run)
      .pipe(
        map(
          (schedules: Schedule[]) =>
            schedules?.[0] ?? new Schedule({}, this.validationService),
        ),
      );
  }
  fetchAllByScheduleId(schedule: Schedule) {
    // just returns daily reports by schedule id
    return this.scheduleRecords.fetchReportsByRun(schedule.id).pipe(
      map((reports) => [
        {
          schedule,
          mill: reports.millReports || [],
          kiln: reports.kilnReports || [],
          pkg: reports.pkgReports || [],
        },
      ]),
    );
  }
  async fetchLabReportsByRun(runnumber: string) {
    //lab reports by run (used in run manager)
    const abs = await firstValueFrom(
      this.scheduleRecords.fetchAbsorptionsByRun(runnumber),
    );
    const db = await firstValueFrom(
      this.scheduleRecords.fetchDriedBrickByRun(runnumber),
    );
    return { abs, db };
  }
  onDrop(
    event: DragEvent,
    records: Schedule[],
    dropIndex: number,
    record: Schedule,
  ) {
    // used for index manipulation on plant schedule table
    const draggedIndex = event.dataTransfer?.getData('text');
    if (draggedIndex !== null && draggedIndex !== undefined) {
      const draggedIndexNum = parseInt(draggedIndex, 10);
      const draggedItem = records[draggedIndexNum];
      records.splice(draggedIndexNum, 1);
      records.splice(dropIndex, 0, draggedItem);
    }
  }
  uncompleteRecord(record: Schedule) {
    // user uncompletes a schedule to add it back to the plant schedule
    this.dialogService.confirmAction(
      () => this.updateCompletedRecord(record),
      'Confirm Uncompletion',
      'Are you sure you want to mark this as uncomplete?',
      () => {
        record.completed = true;
      },
    );
  }
  updateCompletedRecord(record: Schedule) {
    record.completed = false; //reverts back to false
    record.completedBy = '';
    const changes = [
      { key: 'completed', value: false },
      { key: 'completedBy', value: '' },
    ];
    this.prodRecordForm.performUpdateSnackbar(record, true, changes);
  }
  async createBackupSchedule(record: Schedule) {
    const workflow: WorkflowStep[] = [
      {
        key: 'create',
        title: 'Creating Backup Run',
        execute: async () =>
          firstValueFrom(this.scheduleRecords.createRecord(record)),
      },
    ];
    const result = await this.workflowRunner.run(workflow);
    if (result.success) {
      this.dialogService.showSuccess('Backup Schedule Added');
    } else {
      this.dialogService.showError('Creation failed');
    }

    return {
      record,
      recordId: record.id,
      result,
    };
  }
  async updateSchedule(record: Schedule) {
    const original = await this.scheduleRecords.fetchScheduleById(record.id);

    const changes = this.compare.getChangedFieldsWithValues(original, record);
    const workflow: WorkflowStep[] = [
      {
        key: 'update',
        title: 'Update Schedule Run',
        execute: async () =>
          firstValueFrom(this.scheduleRecords.updateRecord(record, changes)),
      },
    ];
    const result = await this.workflowRunner.run(workflow);
    if (result.success) {
      this.dialogService.showSuccess('Schedule Updated');
    } else {
      this.dialogService.showError('Update failed');
    }

    return {
      record,
      recordId: record.id,
      result,
    };
  }
  async deleteBackupSchedule(record: Schedule) {
    const workflow: WorkflowStep[] = [
      {
        key: 'delete',
        title: 'Deleting Backup Run',
        execute: async () =>
          firstValueFrom(this.scheduleRecords.deleteRecord(record.id)),
      },
    ];
    const result = await this.workflowRunner.run(workflow);
    if (result.success) {
      this.dialogService.showSuccess('Backup Removal Success');
    } else {
      this.dialogService.showError('Deletion failed');
    }

    return {
      record,
      recordId: record.id,
      result,
    };
  }
  calculateTotal(property: string, records: any[]): number {
    return this.calc.calculateTotal(property, records);
  }
  async productCodeDialog(record?: any, mill?: number) {
    const field = await this.fieldService.getFieldType('schedule/allproducts');
    if (!field) return;
    const fullField = await firstValueFrom(
      this.fieldService.loadFieldData(field),
    );
    const query = record?.schedule?.productCode
      ? record?.schedule?.productCode
      : mill === 55
        ? '55S'
        : '';
    const group = record instanceof PkgGroup || record instanceof Schedule;
    const { FieldEditorComponent } =
      await import('../../../../../widget/field-editor/field-editor.component');
    this.dialogService.openShellDialog(
      FieldEditorComponent,
      {
        field: { ...fullField, showQuery: true, showSelect: group },
        record,
        mill,
        query,
      },
      'Product Codes',
      true,
      undefined,
      'content-only-dialog midscreen',
    );
  }
  get EmailLoading() {
    //email variable to stop users from over clicking
    return this.emailLoading;
  }
  async getSchedulesForLocation(location: number) {
    // grabs all schedules for specific location
    let mills = entities
      .filter((e) => e.dept === Departments.Moulding)
      .flatMap((e) => {
        return {
          id: e.values[0].entityId,
          machineNo: e.values.flatMap((v) => v.entityId),
          mill: e.mills[0],
          plantName: e.plantName,
          location: e.location,
          identifier: e.values[0].entityId,
          assets: e.values,
        };
      })
      .filter((m) => m.id !== 0);
    const records = await Promise.all(
      mills.map((mill) => this.createScheduleAndAnnouncement(mill.mill)),
    );
    return this.filterSchedulesByLocation(records, location);
  }
  private filterSchedulesByLocation(records: any, location: number) {
    return records.filter((rec: any) =>
      location !== 0 ? rec.location === location : rec,
    );
  }
  generateScheduleEmailContent(schedules: Schedule[]) {
    // incomplete schedules
    return schedules
      .map((schedule, index) =>
        this.scheduleRecords.generateScheduleContent(
          schedule,
          index === 0,
          'standard',
        ),
      )
      .join('');
  }
  private generateCompletedContent(schedules: any) {
    // completed schedules
    return schedules
      .map((schedule: any, index: any) =>
        this.scheduleRecords.generateScheduleContent(
          schedule,
          index === 0,
          'completed',
        ),
      )
      .join('');
  }
  generatePdf(content: string, title: string, recordType: string) {
    this.pdf.generatePDF(content, title, recordType);
  }
  get PrintLoading() {
    return this.printLoading; // print variable to stop over loading
  }
  async generateCompletedSchedulesPDF(
    schedules: { mill: string; schedules: any }[],
    year: number,
  ) {
    // generates pdf doc of completed schedules for the year
    this.printLoading = true;
    const fullContent = this.generateCompletedContent(schedules);
    const filename = `Completed_Schedules_${year}.pdf`;
    this.pdf.generatePDF(fullContent, filename, 'kiln', 'Completed-Schedules');
    this.printLoading = false;
  }
  getCompletedSchedulesTable() {
    const columnNames: TableColumn[] = [
      this.cellService.createTextColumn('run', 'Run #'),
      this.cellService.createTextColumn('size', 'Size'),
      this.cellService.createTextColumn('brick', 'Brick'),
      this.cellService.createTextColumn('productCode', 'Product Code'),
      this.cellService.createTextColumn('attribute', 'Attribute'),
      this.cellService.createTextColumn('specialShapes', 'Shapes'),
      this.cellService.createTextColumn('comments', 'Comments'),
      {
        key: 'completed',
        header: { name: 'Completed', type: 'text' },
        cell: {
          type: 'checkbox',
          fn: (r) => this.uncompleteRecord(r),
          disabled: () =>
            !this.userRoleService.canAccessActions(7, [1, 2, 3, 4]),
        },
        rowspan: 2,
      },
      this.cellService.createTextColumn('completedBy', 'Completed By'),
      ...(!this.optimization.isCompact
        ? [
            this.cellService.createButtonColumn(
              'pi pi-flag',
              (r) => this.openNotesSnapshot(r),
              'Notes',
              {
                cell: {
                  type: 'button',
                  fn: (r, i) => this.openNotesSnapshot(r),
                  icon: (r) =>
                    r.notes && r.notes.length > 0
                      ? 'pi pi-flag-fill'
                      : 'pi pi-flag',
                  severity: (r) =>
                    r.notes && r.notes.length > 0 ? 'danger' : 'info',
                },
              },
            ),
          ]
        : []),
    ];
    return { columnNames };
  }
  openNotesSnapshot(record: any, user?: any) {
    const username = user ? user : this.userRoleService.getUser()?.name;
    this.viewNotesSnapshot(record, username);
  }
  getRowClasses(record: any, index?: number, records?: any[]): any {
    const attr = record.attribute?.toUpperCase() || '';

    const hasTumbled = attr.includes('TUMBLED');
    const hasCored = attr.includes('CORED');
    const hasSolid = attr.includes('SOLID');

    const checkMatchingProductcodes = (
      record1: Schedule,
      record2: Schedule,
    ) => {
      if (!record1 || !record2) return false;
      // Extract first two characters
      const firstTwo1 = record1?.productCode.slice(0, 2);
      const firstTwo2 = record2?.productCode.slice(0, 2);
      // Extract last three numeric characters (ignoring trailing letters)
      const lastThree1 = record1?.productCode.replace(/[^0-9]+$/, '').slice(-3);
      const lastThree2 = record2?.productCode.replace(/[^0-9]+$/, '').slice(-3);
      return (
        firstTwo1 === firstTwo2 &&
        lastThree1 === lastThree2 &&
        record1.size === record2.size
      );
    };

    let group = false;
    if (records && typeof index === 'number') {
      group =
        checkMatchingProductcodes(records[index], records[index + 1]) ||
        checkMatchingProductcodes(records[index - 1], records[index]);
    }

    return {
      group,
      flagged: record.flagged,
      'tumbled-solid': hasTumbled && hasSolid,
      solids: hasSolid && !(hasTumbled && hasSolid),
      cored: hasCored && !(hasTumbled && hasCored),
      tumbled: hasTumbled && !hasCored && !hasSolid,
      shapes: record.specialShapes,
      'add-new': record.id === 0 && record.productCode !== 'z',
    };
  }
  getRecordClasses = (record: any, index: number) => {
    const classes: string[] = [];
    const attr = record.attribute?.toUpperCase() || '';
    const hasTumbled = attr.includes('TUMBLED');
    const hasCored = attr.includes('CORED');
    const hasSolid = attr.includes('SOLID');
    if (hasTumbled && hasCored) {
      classes.push('tumbled-cored row');
    } else if (hasSolid && !(hasTumbled && hasSolid)) {
      classes.push('solids row');
    } else if (hasCored && !(hasTumbled && hasCored)) {
      classes.push('cored row');
    } else if (hasTumbled && !hasCored && !hasSolid) {
      classes.push('tumbled row');
    } else if (hasTumbled && hasSolid) {
      classes.push('tumbled-solid');
    }
    return classes.join(' ');
  };
  async viewNotesSnapshot(record: Schedule | ShapesOrder, user: string) {
    const isSchedule = 'run' in record; // crude type guard for Schedule
    const notes =
      record.notes && record.notes.length > 0
        ? record.notes
        : [
            new ScheduleComments(
              isSchedule
                ? { schedule: record, isUpdating: true, user }
                : { shapes: record, isUpdating: true, user },
              this.validationService,
            ),
          ];
    const title = isSchedule
      ? `Notes for ${record.run}`
      : `Notes for ${record.order}`;

    const { NotesComponent } =
      await import('../../../../../widget/notes/notes.component');
    this.dialogService.openShellDialog(
      NotesComponent,
      { notes, schedule: record, showButton: true },
      title,
      true,
      '',
      'content-only-dialog smallScreen',
    );
  }
}
